import React, { useEffect, useState } from "react";
import CheckoutForm from "./CheckoutForm";
import OrderSummary from "./OrderSummary";
import { usePatientOrder } from "../data/patientOrder";
import Logo from "images/pop-logo.svg";
import Notification from "../components/notification";
import FaqModal from "./FaqModal";
import OrderingPauseModal from "./OrderingPauseModal";

// A dismissed notice is remembered per pause, per browser. The server never
// reads this flag, so localStorage rather than a cookie — no point adding
// weight to every request.
const noticeDismissalKey = (pauseId) => `ordering_pause_notice_${pauseId}`;

export default function PatientCheckout() {
  const params = new URLSearchParams(window.location.search);
  const token = params.get("token");
  const { patientOrder, isLoading, isError, mutate } = usePatientOrder(token);
  const [orderingStatus, setOrderingStatus] = useState(null);
  const [pauseModalOpen, setPauseModalOpen] = useState(false);

  useEffect(() => {
    document.documentElement.style.setProperty("--body-bg-color", "#f9fafb");
  }, []);

  useEffect(() => {
    if (!token) return undefined;

    let cancelled = false;

    fetch(`/api/patient/ordering-status?token=${encodeURIComponent(token)}`)
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (cancelled || !data) return;

        setOrderingStatus(data);

        const notice = data.notice;
        if (!notice) return;

        // A block shows on every visit — it is the reason checkout does not
        // work, and a patient who dismissed it yesterday still needs it today.
        // A notice shows once per pause, so an eight-day notice window doesn't
        // train people to dismiss modals unread.
        if (notice.variant === "blocked") {
          setPauseModalOpen(true);
        } else if (
          window.localStorage.getItem(noticeDismissalKey(notice.id)) !== "true"
        ) {
          setPauseModalOpen(true);
        }
      })
      .catch(() => {
        // Ordering-status is advisory; the payment endpoint is the source of
        // truth. A failed lookup must never keep a patient from checking out.
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  const closePauseModal = () => {
    const notice = orderingStatus?.notice;

    if (notice && notice.variant === "notice") {
      window.localStorage.setItem(noticeDismissalKey(notice.id), "true");
    }

    setPauseModalOpen(false);
  };

  return (
    <>
      <div className="mx-auto max-w-7xl pb-8">
        <div className="flex justify-between flex-wrap">
          <p className="text-3xl font-bold tracking-tight integria-green pb-1 order-2 sm:order-1">
            Your Prescription
          </p>
          <img
            src={Logo}
            className="object-right object-contain h-8 sm:h-12 order-1 sm:order-2 mb-3 sm:mb-0"
            alt="Integria Logo"
          />
        </div>
        <div className="mt-2 border-b border-gray-200 pb-2 text-sm flex justify-between flex-col sm:flex-row">
          <div className={`flex ${isLoading ? "invisible" : ""}`}>
            <div className="mr-2 hidden sm:block">
              <p className="text-gray-500 mb-1">Practitioner</p>
              <p className="text-gray-500">Patient</p>
            </div>
            <div>
              <div className="flex mb-1">
                <p className="font-medium text-gray-900">
                  {patientOrder?.practitioner?.first_name}&nbsp;
                  {patientOrder?.practitioner?.last_name}
                </p>
                <p>
                  <span className={`mx-2 text-gray-400`}>&middot;</span>
                </p>
                <a
                  href={`mailto:${patientOrder?.practitioner.email}`}
                  className="font-medium"
                >
                  <p>{patientOrder?.practitioner.email}&nbsp;</p>
                </a>
              </div>
              <p className="font-medium text-gray-900">
                {patientOrder?.patient.first_name}&nbsp;
                {patientOrder?.patient.last_name}
              </p>
            </div>
          </div>
          <p className="font-medium text-gray-900 flex items-end mt-2 sm:mt-0">
            <time dateTime={patientOrder?.created_at}>
              {patientOrder?.created_at}&nbsp;
            </time>
          </p>
        </div>
      </div>
      <main className="lg:min-h-full grid lg:grid-cols-2 lg:gap-x-12 xl:gap-x-16">
        <div>
          <OrderSummary token={token} />
          <div className="mt-4 flex flex-col gap-4">
            <FaqModal />
            <a
              href="/patient-terms.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex text-sm text-gray-500 hover:text-gray-700 cursor-pointer"
            >
              Terms and Conditions
            </a>
          </div>
        </div>
        <div>
          <CheckoutForm
            token={token}
            orderingBlocked={Boolean(orderingStatus?.ordering_blocked)}
          />
        </div>
      </main>
      {patientOrder?.practitioner && (
        <Notification
          cookieName="WebsiteUpdateNotification052025-2"
          title="Website Update"
          content={
            patientOrder.practitioner.company_code === "ihal"
              ? "We've upgraded our checkout to give you a smoother experience."
              : "Added:\n- Authority to leave parcels at your door\n- Option to opt out of restock emails.\n- New simplified web address"
          }
        />
      )}
      {orderingStatus?.notice && (
        <OrderingPauseModal
          open={pauseModalOpen}
          variant={orderingStatus.notice.variant}
          title={orderingStatus.notice.title}
          message={orderingStatus.notice.message}
          onClose={closePauseModal}
        />
      )}
    </>
  );
}
